import { useDebounce } from 'ahooks';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { messageOnError } from '@/common/utils';

import { DomainVisitors } from '@/modules/Domains';
import { PageNode } from '@/modules/Pages';
import {
  DEFAULT_REDIRECT_MATCH_MODE,
  DEFAULT_REDIRECT_STATUS_CODE,
  RedirectEndpointEnum,
  RedirectEndpointType,
  RedirectMatchMode,
  RedirectsQueryKeys,
  useBulkCreateRedirectsByPagesMutation,
  useGetRedirectsPagesPathsQuery,
} from '@/modules/Sites/Redirects';

import { RedirectsBulkByPagesView } from './RedirectsBulkByPagesView';

interface Props {
  siteId: string;
}

export const RedirectsBulkByPagesContainer: FC<Props> = memo(({ siteId }) => {
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();

  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, { wait: 400 });

  const [selectedItems, setSelectedItems] = useState<Record<string, string>>({});

  const [targetType, setTargetType] = useState<RedirectEndpointType>(RedirectEndpointEnum.Path);
  const [targetNode, setTargetNode] = useState<Pick<PageNode, 'id' | 'path'> | undefined>(undefined);
  const [pathTo, setPathTo] = useState('');
  const [statusCode, setStatusCode] = useState<number>(DEFAULT_REDIRECT_STATUS_CODE);
  const [matchMode, setMatchMode] = useState<RedirectMatchMode>(DEFAULT_REDIRECT_MATCH_MODE);
  const [forVisitor, setForVisitor] = useState<DomainVisitors>(DomainVisitors.Any);
  const [isActive, setIsActive] = useState(true);

  const { data: pagesPathsPage, isLoading: isPagesPathsLoading } = useGetRedirectsPagesPathsQuery(
    siteId,
    { page: currentPage },
    debouncedSearch || undefined
  )();

  const { mutate: bulkCreate, isLoading } = useBulkCreateRedirectsByPagesMutation(siteId)({
    onSuccess: async ({ createdCount, skippedCount }) => {
      await Promise.all([
        queryClient.refetchQueries([RedirectsQueryKeys.GetAll, siteId]),
        queryClient.refetchQueries([RedirectsQueryKeys.GetAllList, siteId]),
        queryClient.refetchQueries([RedirectsQueryKeys.Conflicts, siteId]),
      ]);

      appMessageApi.success(
        `Створено редіректів: ${createdCount}` + (skippedCount ? `, пропущено дублів: ${skippedCount}` : '')
      );

      setSelectedItems({});
      setPathTo('');
      setTargetNode(undefined);
    },
    onError: (err) => messageOnError(err, appMessageApi),
  });

  const targetPath = pathTo.trim().toLowerCase();
  const isTargetPathValid = /^\/[\w./=?\-]*$/i.test(targetPath);
  const isTargetValid = targetType === RedirectEndpointEnum.Node ? Boolean(targetNode) : isTargetPathValid;

  const selectedIds = useMemo(() => Object.keys(selectedItems), [selectedItems]);

  const items = useMemo(
    () =>
      isTargetValid
        ? Object.entries(selectedItems)
            .filter(([id, path]) =>
              targetType === RedirectEndpointEnum.Node ? id !== targetNode?.id : path !== targetPath
            )
            .map(([id, path]) => ({ id, path }))
        : [],
    [selectedItems, targetType, targetNode, targetPath, isTargetValid]
  );

  const skippedSelfCount = selectedIds.length - items.length - (isTargetValid ? 0 : selectedIds.length);

  const targetLabel = targetType === RedirectEndpointEnum.Node ? (targetNode?.path ?? '') : targetPath;

  const handleSubmit = useCallback(() => {
    if (!items.length) return;

    bulkCreate({
      redirects: items.map(({ id }) => ({
        pageNodeId: id,
        targetType,
        targetNodeId: targetType === RedirectEndpointEnum.Node ? targetNode?.id : undefined,
        targetPath: targetType === RedirectEndpointEnum.Path ? targetPath : undefined,
        statusCode,
        matchMode,
        forVisitor,
        isActive,
      })),
    });
  }, [items, targetType, targetNode, targetPath, statusCode, matchMode, forVisitor, isActive, bulkCreate]);

  const handleChangeSearch = useCallback((value: string) => {
    setSearch(value);
    setCurrentPage(1);
  }, []);

  const handleChangeTargetType = useCallback((value: RedirectEndpointType) => {
    setTargetType(value);
    setPathTo('');
    setTargetNode(undefined);
  }, []);

  const handleToggleNode = useCallback((node: Pick<PageNode, 'id' | 'path'>, checked: boolean) => {
    setSelectedItems((prev) => {
      if (checked) return { ...prev, [node.id]: node.path };

      const { [node.id]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const handleToggleSelectAllOnPage = useCallback(
    (checked: boolean) => {
      const pageItems = pagesPathsPage?.items ?? [];

      setSelectedItems((prev) => {
        const next = { ...prev };

        if (checked) pageItems.forEach(({ id, path }) => { next[id] = path; });
        else pageItems.forEach(({ id }) => { delete next[id]; });

        return next;
      });
    },
    [pagesPathsPage]
  );

  return (
    <RedirectsBulkByPagesView
      siteId={ siteId }
      pageItems={ pagesPathsPage?.items }
      totalItems={ pagesPathsPage?.meta.totalItems ?? 0 }
      currentPage={ currentPage }
      isPagesPathsLoading={ isPagesPathsLoading }
      search={ search }
      onChangeSearch={ handleChangeSearch }
      onChangePage={ setCurrentPage }
      selectedIds={ selectedIds }
      onToggleNode={ handleToggleNode }
      onToggleSelectAllOnPage={ handleToggleSelectAllOnPage }
      targetType={ targetType }
      onChangeTargetType={ handleChangeTargetType }
      targetNodeId={ targetNode?.id }
      onChangeTargetNode={ setTargetNode }
      pathTo={ pathTo }
      onChangePathTo={ setPathTo }
      targetLabel={ targetLabel }
      isTargetValid={ isTargetValid }
      statusCode={ statusCode }
      onChangeStatusCode={ setStatusCode }
      matchMode={ matchMode }
      onChangeMatchMode={ setMatchMode }
      forVisitor={ forVisitor }
      onChangeForVisitor={ setForVisitor }
      isActive={ isActive }
      onChangeIsActive={ setIsActive }
      items={ items }
      skippedSelfCount={ skippedSelfCount }
      isLoading={ isLoading }
      onSubmit={ handleSubmit }
    />
  );
});
